import React, { useState, useRef } from 'react'
import StyledDialog from '../styled/StyledDialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import StyledButton from '../styled/StyledButton'
import CancelIcon from '@mui/icons-material/Cancel'
import DownloadIcon from '@mui/icons-material/Download'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import Grid from '@mui/material/Grid'

interface IModalPdf {
  pdfUrl: string
  secondPdfUrl?: string
  callback(data: any): void
}

interface PDFControlState {
  scale: number
  isDownloading: boolean
}

const ModalPdf = ({ pdfUrl, secondPdfUrl, callback }: IModalPdf) => {
  const [firstPdfState, setFirstPdfState] = useState<PDFControlState>({
    scale: 1,
    isDownloading: false,
  })
  const [secondPdfState, setSecondPdfState] = useState<PDFControlState>({
    scale: 1,
    isDownloading: false,
  })
  const contentRef = useRef<HTMLDivElement>(null)

  const handleClose = () => {
    callback({ closed: true })
  }

  const handleZoomIn = (isPrimaryPdf: boolean) => {
    const setState = isPrimaryPdf ? setFirstPdfState : setSecondPdfState
    setState((prev) => ({ ...prev, scale: Math.min(prev.scale + 0.2, 3) }))
  }

  const handleZoomOut = (isPrimaryPdf: boolean) => {
    const setState = isPrimaryPdf ? setFirstPdfState : setSecondPdfState
    setState((prev) => ({ ...prev, scale: Math.max(prev.scale - 0.2, 0.5) }))
  }

  const handleDownload = async (url: string, isPrimaryPdf: boolean) => {
    const setState = isPrimaryPdf ? setFirstPdfState : setSecondPdfState
    try {
      setState((prev) => ({ ...prev, isDownloading: true }))
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `documento${isPrimaryPdf ? '1' : '2'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Erro ao baixar o PDF:', error)
    } finally {
      setState((prev) => ({ ...prev, isDownloading: false }))
    }
  }

  const renderPDFControls = (isPrimaryPdf: boolean, url: string) => {
    const state = isPrimaryPdf ? firstPdfState : secondPdfState
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <StyledButton
          variant='contained'
          color='primary'
          startIcon={state.isDownloading ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
          onClick={() => handleDownload(url, isPrimaryPdf)}
          disabled={state.isDownloading}
        >
          {state.isDownloading ? 'Baixando...' : 'Baixar'}
        </StyledButton>
      </Box>
    )
  }

  const renderIframeViewer = (url: string, scale: number) => {
    const pageWidth = contentRef.current ? contentRef.current.offsetWidth - 42 : 0
      if (!url || url.startsWith('javascript:')) {
    return (
      <Box sx={{
        color: 'red',
        textAlign: 'center',
        width: '100%',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '1.2rem',
      }}>
        Nenhum arquivo anexado!
      </Box>
    )
  }
    return (
      <iframe
        src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${url}`}
        width={pageWidth > 0 ? pageWidth * scale : '100%'}
        height='100%'
        style={{ border: 'none', flexGrow: 1 }}
      />
    )
  }

  return (
    <StyledDialog open fullWidth maxWidth='lg'>
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: secondPdfUrl ? '1fr 1fr 1fr' : '1fr auto 1fr',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {secondPdfUrl ? (
          <>

            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>Visualização de PDF</Box>

          </>
        ) : (
          <>
            <Box />
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'left' }}>Visualização de PDF</Box>

          </>
        )}
      </DialogTitle>
      <DialogContent ref={contentRef} sx={{ height: 'calc(100vh - 200px)', padding: '16px', display: 'flex' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {secondPdfUrl ? (
            <>
              <Grid item xs={6} sx={{ display: 'flex', height: '100%' }}>
                {renderIframeViewer(pdfUrl, firstPdfState.scale)}
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', height: '100%' }}>
                {renderIframeViewer(secondPdfUrl, secondPdfState.scale)}
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sx={{ display: 'flex', height: '100%', justifyContent: 'center' }}>
              {renderIframeViewer(pdfUrl, firstPdfState.scale)}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '8px 24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {secondPdfUrl ? (
            <>
              {renderPDFControls(true, pdfUrl)}
              <StyledButton variant='contained' color='inherit' startIcon={<CancelIcon />} onClick={handleClose}>
                Fechar
              </StyledButton>
              {renderPDFControls(false, secondPdfUrl)}
            </>
          ) : (
            <>
              {renderPDFControls(true, pdfUrl)}
              <StyledButton variant='contained' color='inherit' startIcon={<CancelIcon />} onClick={handleClose}>
                Fechar
              </StyledButton>
            </>
          )}
        </Box>
      </DialogActions>
    </StyledDialog>
  )
}

export default ModalPdf
